import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  Skeleton,
  TableBody,
  TableCell,
  TableRow,
  Table,
  TableContainer,
  TableHead,
  TextField,
  useMediaQuery,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  UnfoldMore as SortIcon,
  ExpandLess as SortAscIcon,
  ExpandMore as SortDescIcon,
  DescriptionOutlined,
  SearchRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  ServiceReport,
  ServiceReportSourceType,
  ServiceReportStatusCode,
  ServiceReportsQuery,
  ServiceReportsQueryParameter,
  ServiceReportsState,
} from "../models/ServiceReportsModel";
import SourceTypeBadge from "./SourceTypeBadge";
import {
  changeServiceReportQuery,
  deleteServiceReport,
  getServiceReports,
  selectServiceReport,
  selectHasAnyServiceReports,
} from "../customerMachineServiceReportsSlice";
import ServiceReportShareDialog from "./ServiceReportShareDialog";
import ServiceReportActionsMenu from "./ServiceReportActionsMenu";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import publicServiceReportsApi from "../../../public/api/publicServiceReportsApi";
import RedesignStatusBadge from "../../../shared/components/redesign/RedesignStatusBadge";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import { StatusBadgeColorConfig, accentGradientDeep } from "../../../shared/themes/redesignTokens";
import { dt } from "../utils/dt";
import useTranslation from "../../../shared/hooks/useTranslation";
import Moment from "react-moment";
import accountContext from "../../../account/accountContext";

// ═══════════════════════════════════════════════════════════════
//  STATUS COLORS
// ═══════════════════════════════════════════════════════════════

const statusBadgeColors: Record<number, StatusBadgeColorConfig> = {
  [ServiceReportStatusCode.inProgress]: dt.serviceReports.status.inProgress,
  [ServiceReportStatusCode.solved]: dt.serviceReports.status.solved,
};

const statusLabelMap: Record<number, string> = {
  [ServiceReportStatusCode.inProgress]: "serviceReportStatus.inProgress",
  [ServiceReportStatusCode.solved]: "serviceReportStatus.solved",
};

// ═══════════════════════════════════════════════════════════════
//  TYPES
// ═══════════════════════════════════════════════════════════════

interface ServiceReportsProps {
  machineId: string;
}

interface ColumnDef {
  label: string;
  sortKey?: ServiceReportsQueryParameter;
  width?: string;
  minWidth?: string;
  align?: "left" | "right" | "center";
  hideOnPhone?: boolean;
}

// ═══════════════════════════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════════════════════════

const ServiceReports = ({ machineId }: ServiceReportsProps) => {
  useRedesignSetup();

  const {
    serviceReports,
    serviceReport,
    serviceReportsQuery,
    loading,
  } = useAppSelector<ServiceReportsState>((state: any) => state.customerServiceReports);
  const hasAnyServiceReports = useAppSelector(selectHasAnyServiceReports);
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const navigate = useNavigate();
  const isPhone = useMediaQuery("(max-width:768px)");
  const currentUserId = accountContext.user?.id;
  const initialLoadRef = useRef(true);

  const [isServiceReportShareDialog, setIsServiceReportShareDialog] = useState(false);
  // Note: ServiceReportDialog and SolvingServiceReportDialog replaced by full pages
  const [sortBy, setSortBy] = useState<ServiceReportsQueryParameter>(ServiceReportsQueryParameter.OpenDate);
  const [sortDesc, setSortDesc] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [activeStatus, setActiveStatus] = useState<ServiceReportStatusCode | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Debounced search ───────────────────────────────────────
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        const updatedQuery: ServiceReportsQuery = {
          ...serviceReportsQuery,
          search: value || undefined,
          sortBy,
          sortDesc,
        };
        dispatch(changeServiceReportQuery(updatedQuery));
        fetchReports(updatedQuery);
      }, 300);
    },
    [serviceReportsQuery, sortBy, sortDesc]
  );

  const handleStatusFilter = useCallback(
    (status: ServiceReportStatusCode | null) => {
      setActiveStatus(status);
      const updatedQuery: ServiceReportsQuery = {
        ...serviceReportsQuery,
        statusCodes: status !== null ? [status] : undefined,
        sortBy,
        sortDesc,
      };
      dispatch(changeServiceReportQuery(updatedQuery));
      fetchReports(updatedQuery);
    },
    [serviceReportsQuery, sortBy, sortDesc]
  );

  // ── Column defs ────────────────────────────────────────────
  const columns: ColumnDef[] = [
    { label: translate("status"), sortKey: ServiceReportsQueryParameter.StatusCode, width: "130px" },
    { label: translate("title"), sortKey: ServiceReportsQueryParameter.Title, minWidth: "240px" },
    { label: translate("number"), sortKey: ServiceReportsQueryParameter.Number, width: "140px", hideOnPhone: true },
    { label: translate("openDate"), sortKey: ServiceReportsQueryParameter.OpenDate, width: "150px", hideOnPhone: true },
    { label: translate("closeDate"), sortKey: ServiceReportsQueryParameter.CloseDate, width: "150px", hideOnPhone: true },
    { label: translate("cost"), sortKey: ServiceReportsQueryParameter.CostValue, width: "120px", align: "right", hideOnPhone: true },
    { label: "", width: "56px" },
  ];

  // ── Handlers ───────────────────────────────────────────────
  const fetchReports = (query: ServiceReportsQuery) => {
    dispatch(getServiceReports({ machineId, query }));
  };

  const handleCreateServiceReport = () => {
    dispatch(selectServiceReport(null));
    navigate(`/machinery/${machineId}/service-reports/new`);
  };

  const handleSort = (key: ServiceReportsQueryParameter) => {
    const newDesc = sortBy === key ? !sortDesc : true;
    setSortBy(key);
    setSortDesc(newDesc);
    const updatedQuery = { ...serviceReportsQuery, sortBy: key, sortDesc: newDesc };
    dispatch(changeServiceReportQuery(updatedQuery));
    fetchReports(updatedQuery);
  };

  const handleShare = (item: ServiceReport) => {
    dispatch(selectServiceReport(item));
    setIsServiceReportShareDialog(true);
  };

  const handleDownloadPdf = (item: ServiceReport) => {
    publicServiceReportsApi.getServiceReportPdfLink(item.number, `service-report-${item.number}.pdf`);
  };

  const handleEdit = (item: ServiceReport) => {
    dispatch(selectServiceReport(item));
    navigate(`/machinery/${machineId}/service-reports/new`);
  };

  const handleMarkAsSolved = (item: ServiceReport) => {
    navigate(`/machinery/${machineId}/service-reports/${item.number}/solve`);
  };

  const handleEditSolvedReport = (item: ServiceReport) => {
    navigate(`/machinery/${machineId}/service-reports/${item.number}/solve`);
  };

  const handleResultServiceReport = (item: ServiceReport) => {
    navigate(`/machinery/${machineId}/service-reports/${item.number}`);
  };

  const handleDeleteServiceReport = (item: ServiceReport) => {
    confirm({
      title: translate("deleteServiceReportTitle"),
      text: translate("deleteServiceReportText", { number: item.number }),
      confirmButton: translate("yesDelete"),
      closeButton: tCommon("common.no"),
      severity: "error",
      onConfirm: async () => {
        await dispatch(deleteServiceReport(item.number));
        await fetchReports(serviceReportsQuery);
      },
    });
  };

  const handleCreateServiceRequest = (item: ServiceReport) => {
    navigate(`/service-requests/new?machine=${machineId}&report=${item.number}`);
  };

  // ── Effects ────────────────────────────────────────────────
  useEffect(() => {
    const initialQuery = { sortBy, sortDesc };
    dispatch(changeServiceReportQuery(initialQuery));
    fetchReports(initialQuery);
  }, [machineId]);

  useEffect(() => {
    if (serviceReports && initialLoadRef.current) {
      initialLoadRef.current = false;
    }
  }, [serviceReports]);

  // ── Sort icon helper ───────────────────────────────────────
  const renderSortIcon = (key?: ServiceReportsQueryParameter) => {
    if (!key) return null;
    if (sortBy !== key) return <SortIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.4 }} />;
    return sortDesc
      ? <SortDescIcon sx={{ fontSize: 16, ml: 0.5, color: dt.blue }} />
      : <SortAscIcon sx={{ fontSize: 16, ml: 0.5, color: dt.blue }} />;
  };

  // ── Format cost ────────────────────────────────────────────
  const formatCost = (value?: number) => {
    if (value == null || value === 0) return "-";
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);
  };

  // ═══════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════

  const showSkeleton = loading && !serviceReports;
  const showEmpty = !loading && hasAnyServiceReports === false;
  const showNoResults = !loading && serviceReports?.length === 0 && hasAnyServiceReports !== false;
  const showTable = serviceReports && serviceReports.length > 0;

  return (
    <Box sx={{ fontFamily: dt.font }}>
      {/* ── Header row ─────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateServiceReport}
          sx={{
            background: dt.serviceReports.createBtnGradient,
            borderRadius: dt.radiusPill,
            textTransform: "none",
            fontFamily: dt.font,
            fontWeight: 600,
            fontSize: "13px",
            px: 3,
            py: 1,
            boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
            "&:hover": {
              background: accentGradientDeep,
              boxShadow: "0 4px 14px rgba(52,152,219,0.35)",
            },
          }}
        >
          {translate("createServiceReport")}
        </Button>
      </Box>

      {/* ── Search bar + Status filter pills ──────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          placeholder={translate("searchReports")}
          value={searchText}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ fontSize: 20, color: dt.textMuted }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: "1 1 240px",
            maxWidth: isPhone ? "100%" : "320px",
            "& .MuiOutlinedInput-root": {
              fontFamily: dt.font,
              fontSize: "13px",
              borderRadius: dt.radiusSm,
              backgroundColor: "#FAFBFC",
              "& fieldset": { borderColor: dt.cardBorder },
              "&:hover fieldset": { borderColor: dt.blue },
              "&.Mui-focused fieldset": { borderColor: dt.blue },
            },
          }}
        />
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          <Chip
            label={translate("allStatuses")}
            size="small"
            onClick={() => handleStatusFilter(null)}
            sx={{
              fontFamily: dt.font,
              fontWeight: 600,
              fontSize: "12px",
              borderRadius: "16px",
              cursor: "pointer",
              transition: "all 0.15s ease",
              ...(activeStatus === null
                ? {
                    backgroundColor: dt.blue,
                    color: "#FFFFFF",
                    "&:hover": { backgroundColor: dt.blueHover },
                  }
                : {
                    backgroundColor: "transparent",
                    color: dt.textSecondary,
                    border: `1px solid ${dt.cardBorder}`,
                    "&:hover": { backgroundColor: "rgba(52,152,219,0.06)", borderColor: dt.blue },
                  }),
            }}
          />
          <Chip
            label={translate("serviceReportStatus.inProgress")}
            size="small"
            onClick={() => handleStatusFilter(ServiceReportStatusCode.inProgress)}
            sx={{
              fontFamily: dt.font,
              fontWeight: 600,
              fontSize: "12px",
              borderRadius: "16px",
              cursor: "pointer",
              transition: "all 0.15s ease",
              ...(activeStatus === ServiceReportStatusCode.inProgress
                ? {
                    backgroundColor: dt.serviceReports.status.inProgress.bg,
                    color: dt.serviceReports.status.inProgress.text,
                    border: `1px solid ${dt.serviceReports.status.inProgress.border}`,
                  }
                : {
                    backgroundColor: "transparent",
                    color: dt.textSecondary,
                    border: `1px solid ${dt.cardBorder}`,
                    "&:hover": { backgroundColor: dt.serviceReports.status.inProgress.bg },
                  }),
            }}
          />
          <Chip
            label={translate("serviceReportStatus.solved")}
            size="small"
            onClick={() => handleStatusFilter(ServiceReportStatusCode.solved)}
            sx={{
              fontFamily: dt.font,
              fontWeight: 600,
              fontSize: "12px",
              borderRadius: "16px",
              cursor: "pointer",
              transition: "all 0.15s ease",
              ...(activeStatus === ServiceReportStatusCode.solved
                ? {
                    backgroundColor: dt.serviceReports.status.solved.bg,
                    color: dt.serviceReports.status.solved.text,
                    border: `1px solid ${dt.serviceReports.status.solved.border}`,
                  }
                : {
                    backgroundColor: "transparent",
                    color: dt.textSecondary,
                    border: `1px solid ${dt.cardBorder}`,
                    "&:hover": { backgroundColor: dt.serviceReports.status.solved.bg },
                  }),
            }}
          />
        </Box>
      </Box>

      {/* ── Skeleton loading ───────────────────────────── */}
      {showSkeleton && (
        <Box sx={{ mt: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={52}
              sx={{
                borderRadius: dt.radiusSm,
                mb: 1,
                animation: "fadeUp 0.4s ease-out forwards",
                animationDelay: `${i * 80}ms`,
                opacity: 0,
              }}
            />
          ))}
        </Box>
      )}

      {/* ── Empty state (no reports at all) ────────────── */}
      {showEmpty && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 8,
            gap: 2,
          }}
        >
          <DescriptionOutlined sx={{ fontSize: 56, color: dt.textMuted, opacity: 0.5 }} />
          <Box sx={{ fontFamily: dt.font, fontSize: "16px", fontWeight: 600, color: dt.textPrimary }}>
            {translate("noServiceReportsYet")}
          </Box>
          <Box sx={{ fontFamily: dt.font, fontSize: "14px", color: dt.textSecondary, textAlign: "center" }}>
            {translate("noServiceReportsYetDescription")}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateServiceReport}
            sx={{
              mt: 1,
              background: dt.serviceReports.createBtnGradient,
              borderRadius: dt.radiusPill,
              textTransform: "none",
              fontFamily: dt.font,
              fontWeight: 600,
              fontSize: "13px",
              px: 3,
              py: 1,
              boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
              "&:hover": {
                background: accentGradientDeep,
              },
            }}
          >
            {translate("createServiceReport")}
          </Button>
        </Box>
      )}

      {/* ── No results from filter ─────────────────────── */}
      {showNoResults && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 8,
            gap: 1.5,
          }}
        >
          <DescriptionOutlined sx={{ fontSize: 48, color: dt.textMuted, opacity: 0.4 }} />
          <Box sx={{ fontFamily: dt.font, fontSize: "15px", fontWeight: 500, color: dt.textSecondary }}>
            {translate("noServiceReportsFound")}
          </Box>
          <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textMuted }}>
            {translate("noServiceReportsFoundSecondary")}
          </Box>
        </Box>
      )}

      {/* ── Phone card view ────────────────────────────── */}
      {showTable && isPhone && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
          {serviceReports.map((item, idx) => (
            <Box
              key={item.number}
              sx={{
                background: dt.cardBg,
                border: `1px solid ${dt.cardBorder}`,
                borderRadius: dt.radiusSm,
                p: 2,
                animation: initialLoadRef.current ? "none" : `fadeUp 0.35s ease-out forwards`,
                animationDelay: `${idx * 40}ms`,
                opacity: initialLoadRef.current ? 1 : 0,
                transition: "box-shadow 0.2s ease",
                "&:hover": { boxShadow: dt.cardShadowHover },
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                <RedesignStatusBadge
                  label={translate(statusLabelMap[item.statusCode])}
                  colorConfig={statusBadgeColors[item.statusCode]}
                />
                <ServiceReportActionsMenu
                  serviceReport={item}
                  currentUserId={currentUserId}
                  onShare={handleShare}
                  onDownloadPdf={handleDownloadPdf}
                  onEdit={handleEdit}
                  onMarkAsSolved={handleMarkAsSolved}
                  onEditSolvedReport={handleEditSolvedReport}
                  onShowDetails={handleResultServiceReport}
                  onDelete={handleDeleteServiceReport}
                  onCreateServiceRequest={handleCreateServiceRequest}
                />
              </Box>
              <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 600, color: dt.textPrimary, mb: 0.5 }}>
                {item.title}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                <Box sx={{ fontFamily: dt.fontMono, fontSize: "12px", color: dt.textSecondary }}>
                  #{item.number}
                </Box>
                {item.openDate && (
                  <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textMuted }}>
                    <Moment date={item.openDate} format="DD.MM.YYYY" />
                  </Box>
                )}
              </Box>
              {item.cost?.value > 0 && (
                <Box sx={{ fontFamily: dt.fontMono, fontSize: "13px", fontWeight: 600, color: dt.serviceReports.stats.costAccent, mt: 0.5 }}>
                  {formatCost(item.cost.value)}
                </Box>
              )}
              {/* Source type badge (compact) */}
              {item.sourceType && item.sourceType !== ServiceReportSourceType.manual && (
                <Box sx={{ mt: 1 }}>
                  <SourceTypeBadge
                    sourceType={item.sourceType}
                    serviceRequestInternalNumber={item.serviceRequestInternalNumber}
                  />
                </Box>
              )}
              {/* Creator indicator (compact) */}
              {item.createdByName && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    mt: 1,
                    pt: 1,
                    borderTop: `1px solid ${dt.rowBorder}`,
                  }}
                >
                  <Box
                    sx={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: dt.serviceReports.byline.creatorCircleBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        fontFamily: dt.font,
                        fontSize: "9px",
                        fontWeight: 700,
                        color: dt.serviceReports.byline.creatorCircleText,
                      }}
                    >
                      {item.createdByName.split(" ").map((w) => w.charAt(0)).join("").toUpperCase().substring(0, 2)}
                    </Box>
                  </Box>
                  <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textSecondary }}>
                    {item.createdByName}
                    {item.createdByRole && (
                      <Box component="span" sx={{ color: dt.textMuted, ml: 0.5 }}>
                        - {item.createdByRole}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* ── Desktop table view ─────────────────────────── */}
      {showTable && !isPhone && (
        <TableContainer
          sx={{
            borderRadius: dt.radiusSm,
            border: `1px solid ${dt.cardBorder}`,
            overflow: "hidden",
            mt: 1,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: dt.serviceReports.table.headerBg }}>
                {columns.map((col, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      fontFamily: dt.font,
                      fontWeight: 600,
                      fontSize: "12px",
                      color: dt.serviceReports.table.headerColor,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      borderBottom: `1px solid ${dt.rowBorder}`,
                      width: col.width,
                      minWidth: col.minWidth,
                      padding: dt.serviceReports.table.rowPadding,
                      cursor: col.sortKey ? "pointer" : "default",
                      userSelect: "none",
                      textAlign: col.align || "left",
                      "&:hover": col.sortKey ? { color: dt.blue } : {},
                    }}
                    onClick={() => col.sortKey && handleSort(col.sortKey)}
                  >
                    <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                      {col.label}
                      {renderSortIcon(col.sortKey)}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {serviceReports.map((item, idx) => (
                <TableRow
                  key={item.number}
                  sx={{
                    backgroundColor: idx % 2 === 0 ? dt.serviceReports.table.rowOdd : dt.serviceReports.table.rowEven,
                    transition: "background-color 0.15s ease",
                    "&:hover": { backgroundColor: dt.serviceReports.table.rowHover },
                    animation: !initialLoadRef.current ? `fadeUp 0.3s ease-out forwards` : "none",
                    animationDelay: !initialLoadRef.current ? `${idx * 40}ms` : "0ms",
                    opacity: !initialLoadRef.current ? 0 : 1,
                    cursor: "pointer",
                  }}
                  onClick={() => handleResultServiceReport(item)}
                >
                  <TableCell sx={{ padding: dt.serviceReports.table.rowPadding, borderBottom: `1px solid ${dt.rowBorder}` }}>
                    <RedesignStatusBadge
                      label={translate(statusLabelMap[item.statusCode])}
                      colorConfig={statusBadgeColors[item.statusCode]}
                    />
                  </TableCell>
                  <TableCell sx={{ padding: dt.serviceReports.table.rowPadding, borderBottom: `1px solid ${dt.rowBorder}` }}>
                    <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 500, color: dt.textPrimary }}>
                      {item.title}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ padding: dt.serviceReports.table.rowPadding, borderBottom: `1px solid ${dt.rowBorder}` }}>
                    <Box sx={{ fontFamily: dt.fontMono, fontSize: "13px", color: dt.textSecondary }}>
                      {item.number}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ padding: dt.serviceReports.table.rowPadding, borderBottom: `1px solid ${dt.rowBorder}` }}>
                    <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textSecondary }}>
                      {item.openDate ? <Moment date={item.openDate} format="DD.MM.YYYY, HH:mm" /> : "-"}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ padding: dt.serviceReports.table.rowPadding, borderBottom: `1px solid ${dt.rowBorder}` }}>
                    <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textSecondary }}>
                      {item.closeDate ? <Moment date={item.closeDate} format="DD.MM.YYYY, HH:mm" /> : "-"}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ padding: dt.serviceReports.table.rowPadding, borderBottom: `1px solid ${dt.rowBorder}`, textAlign: "right" }}>
                    <Box sx={{ fontFamily: dt.fontMono, fontSize: "13px", fontWeight: 600, color: item.cost?.value > 0 ? dt.serviceReports.stats.costAccent : dt.textMuted }}>
                      {formatCost(item.cost?.value)}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ padding: "8px", borderBottom: `1px solid ${dt.rowBorder}` }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ServiceReportActionsMenu
                      serviceReport={item}
                      currentUserId={currentUserId}
                      onShare={handleShare}
                      onDownloadPdf={handleDownloadPdf}
                      onEdit={handleEdit}
                      onMarkAsSolved={handleMarkAsSolved}
                      onEditSolvedReport={handleEditSolvedReport}
                      onShowDetails={handleResultServiceReport}
                      onDelete={handleDeleteServiceReport}
                      onCreateServiceRequest={handleCreateServiceRequest}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Dialogs ────────────────────────────────────── */}
      {!!serviceReport && (
        <ServiceReportShareDialog
          open={isServiceReportShareDialog}
          serviceReport={serviceReport}
          onClose={() => setIsServiceReportShareDialog(false)}
        />
      )}

      {/* ServiceReportDialog and SolvingServiceReportDialog removed — replaced by full pages */}
    </Box>
  );
};

export default ServiceReports;
